import React from 'react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Pill, Printer, Smartphone } from 'lucide-react';
import axios from 'axios';

export const MedCardsTab = ({ dashboardData, error, filteredMedCards, fmtDateUS, loadDashboard, practice, printPatientReport, safeText, setEditedMedCardContent, setIsEditingMedCards, setViewMedCardsModal, setViewingAssignment, setViewingCards, showSmsSelectionModal, toast }) => (
  <>
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => printPatientReport({
            tabKey: 'medcards',
            reportTitle: 'Med Cards Report',
            subtitle: 'Patient medication card assignment history',
            itemNoun: 'med card',
            items: filteredMedCards,
            headerColor: '#ea580c',
            columns: [
              { key: 'cards', label: 'Med Card(s)' },
              { key: 'count', label: '# Cards' },
              { key: 'dentist', label: 'Provider' },
              { key: 'assigned', label: 'Assigned' },
              { key: 'status', label: 'Status' },
            ],
            rowFor: (a) => {
              const cardNames = (a.medCardNames && a.medCardNames.length > 0)
                ? a.medCardNames.join(', ')
                : `${a.medCardIds?.length || 0} card${(a.medCardIds?.length || 0) === 1 ? '' : 's'}`;
              const status = a.status || 'assigned';
              const badgeClass = status === 'completed' ? 'badge-green' : status === 'sent' ? 'badge-amber' : 'badge-gray';
              return [
                safeText(cardNames),
                a.medCardIds?.length || 0,
                safeText(a.dentistName),
                fmtDateUS(a.assignedDate || a.createdAt),
                `<span class="badge ${badgeClass}">${status.toUpperCase()}</span>`,
              ];
            },
            stats: (items) => [
              { label: 'Total Assignments', value: items.length },
              { label: 'Cards Assigned', value: items.reduce((n, a) => n + (a.medCardIds?.length || 0), 0), color: '#ea580c' },
            ],
          })}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-md"
          data-testid="medcards-print-report-btn"
        >
          <Printer className="h-3.5 w-3.5" />
          Print Med Cards Report
        </button>
      </div>
      {filteredMedCards.map((assignment) => {
        // Handle both old assignments (empty medCardNames) and new ones
        const cardCount = assignment.medCardIds?.length || 0;
        const hasNames = assignment.medCardNames && assignment.medCardNames.length > 0;

        return (
          <div key={assignment.id} className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Pill className="h-6 w-6 text-orange-600" />
                  <h4 className="font-semibold text-lg text-gray-900">Med Card Assignment</h4>
                  <Badge variant="secondary" className="text-sm">{cardCount} Card{cardCount !== 1 ? 's' : ''}</Badge>
                </div>
                <p className="text-base text-gray-600 mb-2">
                  Assigned by: {assignment.dentistName}
                </p>
                <p className="text-sm text-gray-500 mb-3">
                  Date: {new Date(assignment.assignedAt).toLocaleDateString()}
                </p>

                {/* Display card IDs if names aren't available */}
                {hasNames ? (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {assignment.medCardNames.map((name, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className="bg-white text-orange-700"
                      >
                        {name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {assignment.medCardIds?.map((cardId, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className="bg-white text-orange-700 text-xs"
                      >
                        {cardId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            <div className="flex flex-col space-y-1">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    setViewingAssignment(assignment);
                    const token = localStorage.getItem('dentalToken');
                    const cardDetailsPromises = assignment.medCardIds.map(cardId =>
                      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/med-cards/${cardId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                    );
                    const cardResponses = await Promise.all(cardDetailsPromises);
                    const cards = cardResponses.map(res => res.data.data || res.data);
                    setViewingCards(cards);
                    setEditedMedCardContent('');
                    setIsEditingMedCards(false);
                    setViewMedCardsModal(true);
                  } catch (error) {
                    console.error('Error:', error);
                    toast({
                      title: "Error",
                      description: "Failed to load Med Cards",
                      variant: "destructive"
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                View
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('dentalToken');
                    const cardDetailsPromises = assignment.medCardIds.map(cardId =>
                      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/med-cards/${cardId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                    );
                    const cardResponses = await Promise.all(cardDetailsPromises);
                    const cards = cardResponses.map(res => res.data.data || res.data);
                    setViewingCards(cards);
                    setViewingAssignment(assignment);
                    setEditedMedCardContent(cards[0]?.customContent || cards[0]?.baseContent || '');
                    setIsEditingMedCards(true);
                    setViewMedCardsModal(true);
                  } catch (error) {
                    console.error('Error:', error);
                    toast({
                      title: "Error",
                      description: "Failed to load Med Cards",
                      variant: "destructive"
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('dentalToken');
                    const cardDetailsPromises = assignment.medCardIds.map(cardId =>
                      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/med-cards/${cardId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                    );
                    const cardResponses = await Promise.all(cardDetailsPromises);
                    const cards = cardResponses.map(res => res.data.data || res.data);

                    // Import helper
                    const { createPrintHTML } = await import('../MedCardsPatientRecordsHelper');

                    // Remove existing print area
                    const existingPrintArea = document.querySelector('.print-only');
                    if (existingPrintArea) {
                      existingPrintArea.remove();
                    }

                    // Create print area for each card
                    cards.forEach(card => {
                      const printArea = document.createElement('div');
                      printArea.className = 'print-only';
                      printArea.innerHTML = createPrintHTML(card, practice);
                      document.body.appendChild(printArea);
                    });

                    // Print and cleanup
                    setTimeout(() => {
                      window.print();
                      document.querySelectorAll('.print-only').forEach(el => el.remove());
                    }, 100);
                  } catch (error) {
                    console.error('Error:', error);
                  }
                }}

                className="text-sm px-3 py-1 h-8"
              >
                Print
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const patient = dashboardData?.recentPatients?.find(p => p.id === assignment.patientId);
                    if (!patient?.email) {
                      toast({
                        title: "No Email Address",
                        description: "This patient does not have an email address on file",
                        variant: "destructive",
                      });
                      return;
                    }

                    const cardCount = assignment.medCardIds?.length || 0;
                    toast({
                      title: "Sending Emails",
                      description: `Sending ${cardCount} Med Card(s) to ${patient.email}...`,
                    });

                    const token = localStorage.getItem('dentalToken');

                    for (const cardId of assignment.medCardIds) {
                      await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/med-cards/email`,
                        {
                          card_id: cardId,
                          patient_email: patient.email,
                          patient_name: `${patient.firstName} ${patient.lastName}`
                        },
                        {
                          headers: { Authorization: `Bearer ${token}` }
                        }
                      );
                    }

                    toast({
                      title: "✅ Email Sent Successfully",
                      description: `Med Cards sent to ${patient.email}`,
                    });
                    loadDashboard();
                  } catch (error) {
                    console.error('Email error:', error);
                    toast({
                      title: "Email Failed",
                      description: error.response?.data?.detail || "Failed to send emails",
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                Email
              </Button>
              <Button
                data-testid={`text-medcard-${assignment.id}`}
                size="sm"
                variant="outline"
                onClick={() => {
                  const patient = dashboardData?.recentPatients?.find(p => p.id === assignment.patientId);
                  const cardCount = assignment.medCardIds?.length || 0;
                  showSmsSelectionModal(
                    assignment.patientId,
                    'medcard',
                    assignment.id,
                    patient?.cellPhone || patient?.phone,
                    `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
                    `${cardCount} Med Card${cardCount !== 1 ? 's' : ''}`
                  );
                }}
                className="text-sm px-3 py-1 h-8"
              >
                <Smartphone className="h-3 w-3 mr-1" />
                Text
              </Button>
            </div>
          </div>
        </div>
      );
      })}
    </div>
  </>
);
